import { useMemo } from "react";
import { useSelector } from "react-redux";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { WorkflowConfig } from "../../../data-model/workflowConfig";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { RootState } from "../../../redux/store/store";
import { useDraftSubmissionIsPresent } from "./useDraftSubmissionIsPresent";
import { useSubmissionIsPresent } from "./useSubmissionIsPresent";

interface FilingButtonState {
  shouldShowStartFilingButton: boolean;
  startFilingButtonText: string;
}

/**
 * Checks if a submission is present and returns the appropriate button state
 */
const getSubmissionButtonState = (
  submissionIsPresent: boolean,
): FilingButtonState | null => {
  if (submissionIsPresent) {
    return {
      shouldShowStartFilingButton: true,
      startFilingButtonText: "View Submission",
    };
  }
  return null;
};

/**
 * Checks if a draft submission is present and returns the appropriate button state
 */
const getDraftSubmissionButtonState = (
  draftSubmissionIsPresent: boolean,
): FilingButtonState | null => {
  if (draftSubmissionIsPresent) {
    return {
      shouldShowStartFilingButton: true,
      startFilingButtonText: "View Filing",
    };
  }
  return null;
};

/**
 * Checks if the order is eligible for filing based on workflow config and auth status
 */
const isEligibleForFiling = (
  workflowConfig: WorkflowConfig,
  singleMedicalPaOrder: MedicalPaOrder,
): boolean => {
  return !!(
    workflowConfig &&
    workflowConfig?.submission_form_config_id &&
    workflowConfig?.submission_form_config_id !== "" &&
    singleMedicalPaOrder &&
    singleMedicalPaOrder?.auth_on_file?.auth_status ===
      MedicalPaOrdersAuthStatus.AuthRequired
  );
};

/**
 * Gets the button state for starting a new filing
 */
const getStartFilingButtonState = (
  workflowConfig: WorkflowConfig,
  singleMedicalPaOrder: MedicalPaOrder,
): FilingButtonState | null => {
  if (isEligibleForFiling(workflowConfig, singleMedicalPaOrder)) {
    return {
      shouldShowStartFilingButton: true,
      startFilingButtonText: "Start Filing",
    };
  }
  return null;
};

/**
 * Gets the default button state when no other conditions are met
 */
const getDefaultButtonState = (): FilingButtonState => {
  return {
    shouldShowStartFilingButton: false,
    startFilingButtonText: "Go Back",
  };
};

export const useShouldStartFilingAndButtonText = (
  orderId: string,
  singleMedicalPaOrder: MedicalPaOrder,
) => {
  const { data: workflowConfig } = useSelector(
    (state: RootState) => state.workflowConfig,
  );
  const submissionIsPresent = useSubmissionIsPresent();
  const draftSubmissionIsPresent = useDraftSubmissionIsPresent(
    orderId,
    singleMedicalPaOrder,
  );

  const shouldShowStartFilingButtonAndText = useMemo(() => {
    // Check for existing submission first
    const submissionState = getSubmissionButtonState(submissionIsPresent);
    if (submissionState) return submissionState;

    // Check for draft submission
    const draftState = getDraftSubmissionButtonState(draftSubmissionIsPresent);
    if (draftState) return draftState;

    // Check if eligible for new filing
    const startFilingState = getStartFilingButtonState(
      workflowConfig ?? {},
      singleMedicalPaOrder,
    );
    if (startFilingState) return startFilingState;

    // Default state
    return getDefaultButtonState();
  }, [
    workflowConfig,
    singleMedicalPaOrder,
    submissionIsPresent,
    draftSubmissionIsPresent,
  ]);

  return shouldShowStartFilingButtonAndText;
};
